// src/app/modules/demandes/TraitementDemandesRf.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    Calendar,
    AlertCircle,
    ChevronDown,
    Eye,
    MessageSquare,
    User,
} from "lucide-react";

import "../../RessourcesStyles/TraitementDemandes.css";

import {
    getDemandeRfInboxRequest,
    demandeRfDecisionRequest,
} from "../../../../../redux/actions/demandesActions";

const norm = (v) => String(v ?? "").toLowerCase().trim();

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString();
    } catch {
        return String(iso);
    }
};

export default function TraitementDemandesRf() {
    const dispatch = useDispatch();

    const { loading, rfInbox = [], error } = useSelector((s) => s.demandes || {});
    const { currentUser } = useSelector((s) => s.user || {});

    // UI
    const [activeTab, setActiveTab] = useState("enAttente"); // enAttente | approuvees | rejetees
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");

    const [typeFilter, setTypeFilter] = useState("all");
    const [managerFilter, setManagerFilter] = useState("all");
    const [periodFilter, setPeriodFilter] = useState("");

    // Modals
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [motifRejet, setMotifRejet] = useState("");

    useEffect(() => {
        dispatch(getDemandeRfInboxRequest());
    }, [dispatch]);

    // ------------------------------
    // Status helpers (RF) - robust
    // ------------------------------
    const getStatusCode = (d) =>
        String(d?.rfStatus ?? d?.rfStepStatus ?? d?.status ?? "")
            .trim()
            .toUpperCase();

    const isPendingForRf = (d) => {
        const s = getStatusCode(d);
        return s === "PENDING_RF" || s === "PENDING";
    };

    const isApproved = (d) => getStatusCode(d) === "APPROVED";
    const isRejected = (d) => getStatusCode(d) === "REJECTED";

    const counts = useMemo(() => {
        const rows = rfInbox || [];
        const enAttente = rows.filter(isPendingForRf).length;
        const approuvees = rows.filter(isApproved).length;
        const rejetees = rows.filter(isRejected).length;
        return { enAttente, approuvees, rejetees };
    }, [rfInbox]);

    const filtered = useMemo(() => {
        let rows = [...(rfInbox || [])];

        if (activeTab === "enAttente") rows = rows.filter(isPendingForRf);
        if (activeTab === "approuvees") rows = rows.filter(isApproved);
        if (activeTab === "rejetees") rows = rows.filter(isRejected);

        const s = norm(search);
        if (s) {
            rows = rows.filter((d) => {
                const blob = [
                    d.targetUserName,
                    d.createdByName,
                    d.managerName,
                    d.type,
                    d.campagneNom,
                    d.formationNom,
                    d.action,
                    d.period,
                    d.priorite,
                ]
                    .map(norm)
                    .join(" ");
                return blob.includes(s);
            });
        }

        if (typeFilter !== "all") rows = rows.filter((d) => norm(d.type) === norm(typeFilter));
        if (managerFilter !== "all") rows = rows.filter((d) => norm(d.managerName) === norm(managerFilter));
        if (norm(periodFilter)) rows = rows.filter((d) => norm(d.period).includes(norm(periodFilter)));

        return rows;
    }, [rfInbox, activeTab, search, typeFilter, managerFilter, periodFilter]);

    const uniqueTypes = useMemo(() => {
        const set = new Set((rfInbox || []).map((d) => d.type).filter(Boolean));
        return Array.from(set);
    }, [rfInbox]);

    const uniqueManagers = useMemo(() => {
        const set = new Set((rfInbox || []).map((d) => d.managerName).filter(Boolean));
        return Array.from(set);
    }, [rfInbox]);

    const handleViewDetails = (d) => {
        setSelectedDemande(d);
        setShowDetailsModal(true);
    };

    const handleApprove = (d) => {
        if (!d?.id) return;
        dispatch(demandeRfDecisionRequest({ id: d.id, approved: true, comment: "" }));
    };

    const handleReject = (d) => {
        setSelectedDemande(d);
        setMotifRejet("");
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!selectedDemande?.id) return;
        if (!motifRejet.trim()) return;

        dispatch(
            demandeRfDecisionRequest({
                id: selectedDemande.id,
                approved: false,
                comment: motifRejet.trim(),
            })
        );
        setShowRejectModal(false);
    };

    return (
        <div className="tdPage">
            <div className="tdWrap">
                <div className="tdHeader">
                    <h1>Traitement des demandes</h1>

                </div>

                {error ? (
                    <div
                        className="tdCard"
                        style={{
                            borderColor: "#ffd0d0",
                            background: "#fff0f0",
                            color: "#9a2b2b",
                            fontWeight: 800,
                        }}
                    >
                        {String(error)}
                    </div>
                ) : null}

                <div className="tdCard">
                    {/* Tabs */}
                    <div className="tdTabs">
                        <button
                            type="button"
                            className={`tdTab ${activeTab === "enAttente" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("enAttente")}
                        >
                            <AlertCircle size={16} />
                            En attente
                            <span className={`tdCount ${activeTab === "enAttente" ? "is-active" : ""}`}>{counts.enAttente}</span>
                        </button>

                        <button
                            type="button"
                            className={`tdTab ${activeTab === "approuvees" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("approuvees")}
                        >
                            <Check size={16} />
                            Approuvées
                            <span className={`tdCount ${activeTab === "approuvees" ? "is-active" : ""}`}>{counts.approuvees}</span>
                        </button>

                        <button
                            type="button"
                            className={`tdTab ${activeTab === "rejetees" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("rejetees")}
                        >
                            <X size={16} />
                            Rejetées
                            <span className={`tdCount ${activeTab === "rejetees" ? "is-active" : ""}`}>{counts.rejetees}</span>
                        </button>
                    </div>

                    {/* Controls */}
                    <div className="tdControls">
                        <div className="tdSearch">
                            <Search size={16} />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Rechercher (collaborateur, manager, type, campagne, formation...)"
                            />
                        </div>

                        <button type="button" className="tdFilterBtn" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tdChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tdFilters">
                            <div className="tdField">
                                <label>Type</label>
                                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                    <option value="all">Tous</option>
                                    {uniqueTypes.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="tdField">
                                <label>Manager</label>
                                <select value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)}>
                                    <option value="all">Tous</option>
                                    {uniqueManagers.map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="tdField">
                                <label>Période</label>
                                <input
                                    value={periodFilter}
                                    onChange={(e) => setPeriodFilter(e.target.value)}
                                    placeholder="Ex: 2026-01"
                                />
                            </div>

                            <div className="tdField">
                                <label>Réinitialiser</label>
                                <button
                                    type="button"
                                    className="tdFilterBtn"
                                    onClick={() => {
                                        setTypeFilter("all");
                                        setManagerFilter("all");
                                        setPeriodFilter("");
                                        setSearch("");
                                    }}
                                >
                                    Réinitialiser
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Table (gardée telle quelle) */}
                    {loading ? (
                        <div className="tdEmpty">Chargement...</div>
                    ) : filtered.length === 0 ? (
                        <div className="tdEmpty">
                            <AlertCircle size={20} />
                            Aucune demande trouvée.
                        </div>
                    ) : (
                        <div className="tdTableWrap">
                            <table className="tdTable">
                                <thead>
                                <tr>
                                    <th>Collaborateur</th>
                                    <th>Manager</th>
                                    <th>Type</th>
                                    <th>Formation / Action</th>
                                    <th>Période</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((d) => (
                                    <tr key={d.id}>
                                        <td>
                                            <div className="tdName">
                                                <span className="tdPrimary">{d.targetUserName || "—"}</span>
                                            </div>
                                            <div className="tdSub">ID: {d.id}</div>
                                        </td>

                                        <td>
                                            <div className="tdType">
                                                <User size={16} />
                                                <span className="tdMuted">{d.managerName || "—"}</span>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tdType">
                                                <span className="tdDot" style={{ background: "#204385" }} />
                                                <span className="tdStrong">{d.type || "—"}</span>
                                            </div>
                                        </td>

                                        <td className="tdTrunc" title={d.formationNom || d.action || ""}>
                                            {d.formationNom || d.action || "—"}
                                        </td>

                                        <td>
                                            <div className="tdDate">
                                                <Calendar size={16} />
                                                {d.period || "—"}
                                            </div>
                                        </td>

                                        <td className="is-right">
                                            <div className="tdRowActions">
                                                <button
                                                    type="button"
                                                    className="tdIconBtn"
                                                    onClick={() => handleViewDetails(d)}
                                                    title="Voir les détails"
                                                >
                                                    <Eye size={16} />
                                                </button>

                                                {/* Règle demandée: */}
                                                {/* - En attente: Approuver + Rejeter */}
                                                {/* - Approuvées: bouton Rejeter */}
                                                {/* - Rejetées: bouton Approuver */}
                                                {activeTab === "enAttente" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="tdActionBtn tdActionBtn--ok"
                                                            onClick={() => handleApprove(d)}
                                                            title="Approuver"
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="tdActionBtn tdActionBtn--no"
                                                            onClick={() => handleReject(d)}
                                                            title="Refuser"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </>
                                                )}

                                                {activeTab === "approuvees" && (
                                                    <button
                                                        type="button"
                                                        className="tdActionBtn tdActionBtn--no"
                                                        onClick={() => handleReject(d)}
                                                        title="Rejeter (changer la décision)"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}

                                                {activeTab === "rejetees" && (
                                                    <button
                                                        type="button"
                                                        className="tdActionBtn tdActionBtn--ok"
                                                        onClick={() => handleApprove(d)}
                                                        title="Approuver (changer la décision)"
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ===== Details Modal (enrichi) ===== */}
            {showDetailsModal && selectedDemande && (
                <div
                    className="tdModal"
                    onMouseDown={(e) => e.target === e.currentTarget && setShowDetailsModal(false)}
                >
                    <div className="tdModal__panel">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon">
                                    <Eye size={18} />
                                </div>
                                <div>
                                    <div className="tdModal__title">Détails de la demande</div>
                                    <div className="tdModal__sub">ID: {selectedDemande.id}</div>
                                </div>
                            </div>

                            <button type="button" className="tdIconBtn" onClick={() => setShowDetailsModal(false)}>
                                <X size={16} />
                            </button>
                        </div>

                        <div className="tdModal__body">
                            <div className="tdPanel">
                                <div className="tdPanel__title">Informations</div>

                                <div className="tdGrid2">
                                    <div>
                                        <div className="tdInfo__label">Collaborateur</div>
                                        <div className="tdInfo__value">{selectedDemande.targetUserName || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="tdInfo__label">Demandeur</div>
                                        <div className="tdInfo__value">{selectedDemande.createdByName || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="tdInfo__label">Manager</div>
                                        <div className="tdInfo__value">{selectedDemande.managerName || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="tdInfo__label">Campagne</div>
                                        <div className="tdInfo__value">{selectedDemande.campagneNom || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="tdInfo__label">Type</div>
                                        <div className="tdInfo__value">{selectedDemande.type || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="tdInfo__label">Formation / Action</div>
                                        <div className="tdInfo__value">{selectedDemande.formationNom || selectedDemande.action || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="tdInfo__label">Période</div>
                                        <div className="tdInfo__value">{selectedDemande.period || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="tdInfo__label">Priorité</div>
                                        <div className="tdInfo__value">{selectedDemande.priorite ?? "—"}</div>
                                    </div>

                                    <div>
                                        <div className="tdInfo__label">Certifiante</div>
                                        <div className="tdInfo__value">{selectedDemande.certifiante ? "Oui" : "Non"}</div>
                                    </div>
                                    <div>
                                        <div className="tdInfo__label">Mode</div>
                                        <div className="tdInfo__value">{selectedDemande.mode || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="tdInfo__label">Niveau</div>
                                        <div className="tdInfo__value">{selectedDemande.niveau || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="tdInfo__label">Statut</div>
                                        <div className="tdInfo__value">{getStatusCode(selectedDemande) || "—"}</div>
                                    </div>

                                    <div>
                                        <div className="tdInfo__label">Créée le</div>
                                        <div className="tdInfo__value">{fmt(selectedDemande.createdAt)}</div>
                                    </div>
                                    <div>
                                        <div className="tdInfo__label">Mise à jour</div>
                                        <div className="tdInfo__value">{fmt(selectedDemande.updatedAt)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="tdBlock">
                                <div className="tdBlock__title">Objectifs</div>

                                <div className="tdLine">
                                    <MessageSquare size={16} />
                                    <div className="tdLine__content">
                                        <div className="tdLine__label">Objectif 1</div>
                                        <div className="tdLine__value">{selectedDemande.objectifs || "—"}</div>
                                    </div>
                                </div>

                                <div className="tdLine">
                                    <MessageSquare size={16} />
                                    <div className="tdLine__content">
                                        <div className="tdLine__label">Objectif 2</div>
                                        <div className="tdLine__value">{selectedDemande.objectifs2 || "—"}</div>
                                    </div>
                                </div>

                                <div className="tdLine">
                                    <MessageSquare size={16} />
                                    <div className="tdLine__content">
                                        <div className="tdLine__label">Objectif 3</div>
                                        <div className="tdLine__value">{selectedDemande.objectifs3 || "—"}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="tdBlock">
                                <div className="tdBlock__title">Décisions</div>

                                <div className="tdGrid2">
                                    <div>
                                        <div className="tdInfo__label">Décision Manager</div>
                                        <div className="tdInfo__value">{selectedDemande.managerDecisionAt ? "Traitée" : "—"}</div>
                                        <div className="tdSub">
                                            {selectedDemande.managerDecisionByName ? `Par ${selectedDemande.managerDecisionByName}` : ""}
                                            {selectedDemande.managerDecisionAt ? ` • ${fmt(selectedDemande.managerDecisionAt)}` : ""}
                                        </div>
                                        {selectedDemande.managerComment ? (
                                            <div className="tdSub">Commentaire: {selectedDemande.managerComment}</div>
                                        ) : null}
                                    </div>

                                    <div>
                                        <div className="tdInfo__label">Décision RF</div>
                                        <div className="tdInfo__value">{selectedDemande.rfDecisionAt ? "Traitée" : "En attente"}</div>
                                        <div className="tdSub">
                                            {selectedDemande.rfDecisionByName ? `Par ${selectedDemande.rfDecisionByName}` : ""}
                                            {selectedDemande.rfDecisionAt ? ` • ${fmt(selectedDemande.rfDecisionAt)}` : ""}
                                        </div>
                                        {selectedDemande.rfComment ? (
                                            <div className="tdSub">Commentaire: {selectedDemande.rfComment}</div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="tdModal__footer">
                            <button type="button" className="tdBtn tdBtn--ghost" onClick={() => setShowDetailsModal(false)}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Reject Modal ===== */}
            {showRejectModal && selectedDemande && (
                <div
                    className="tdModal"
                    onMouseDown={(e) => e.target === e.currentTarget && setShowRejectModal(false)}
                >
                    <div className="tdModal__panel tdModal__panel--small">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon tdModal__icon--danger">
                                    <MessageSquare size={18} />
                                </div>
                                <div>
                                    <div className="tdModal__title">Refuser la demande</div>
                                    <div className="tdModal__sub">
                                        {selectedDemande.targetUserName || "—"} • {selectedDemande.type || "—"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tdIconBtn" onClick={() => setShowRejectModal(false)}>
                                <X size={16} />
                            </button>
                        </div>

                        <div className="tdModal__body">
                            <label className="tdLabel">
                                Motif <span className="tdReq">*</span>
                            </label>
                            <textarea
                                className="tdTextarea"
                                rows={4}
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                placeholder="Décrire le motif de rejet..."
                            />
                            <div className="tdHelp">Le motif est obligatoire pour le rejet.</div>
                        </div>

                        <div className="tdModal__footer">
                            <button type="button" className="tdBtn tdBtn--ghost" onClick={() => setShowRejectModal(false)}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tdBtn tdBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim()}
                            >
                                <X size={16} />
                                Confirmer le rejet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
